import axios from 'axios';
import { getTenantFromDomain } from '@/utils/tenant';
import { tokenManager } from '@/utils/tokenManager';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://api.thilo.loc:8000/v2/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  // Enable cookies for cross-origin requests
  // Backend sets access_token cookie for browser-based image requests (avatars)
  withCredentials: true,
  // Custom params serializer to handle arrays properly for FastAPI backend
  paramsSerializer: {
    serialize: (params) => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // For arrays, add each value with the same key (FastAPI style)
          // e.g., resource_ids=uuid1&resource_ids=uuid2
          value.forEach((v) => searchParams.append(key, v));
        } else if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      return searchParams.toString();
    },
  },
});

// Request interceptor - add auth token and tenant header
apiClient.interceptors.request.use(
  async (config) => {
    // Get access token (will auto-refresh if close to expiry)
    const token = await tokenManager.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // Only add tenant code header for unauthenticated requests
      // (authenticated users have tenant info in JWT)
      const tenantCode = getTenantFromDomain();
      if (tenantCode) {
        config.headers['X-Tenant-Code'] = tenantCode;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const isLoginRequest = error.config?.url?.includes('/auth/login');

    // Don't intercept 401 errors from login endpoint - let them bubble up
    if (isLoginRequest) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      // Token expired, try to refresh
      try {
        await tokenManager.refreshAccessToken();

        // Get the new token and retry the original request
        const newToken = await tokenManager.getAccessToken();
        if (newToken) {
          error.config.headers.Authorization = `Bearer ${newToken}`;
          return apiClient.request(error.config);
        }
      } catch (refreshError) {
        // Refresh failed, user will be redirected to login by tokenManager
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
